import { BCMSCurrency, BCMSDate, useModel, useParams, useRoute } from "@bcm-dev/bcms-vite";
import { Transaction, Util } from "model";
import React, { useState } from "react";

import { MainContentSection } from "components/shared/containers/MainContentContainer";
import MonthTabsContainer from "components/shared/containers/MonthTabsContainer";
import TransactionView from "components/shared/views/BaseRowView/components/TransactionView";
import { SVGAsset } from "res";
import { GroupDropdownView } from "../..";

import "./styles.css";

/**
 * @function WhenView
 * @return {React.Component}
 * @exported true
 * @description The main component for WhenView
 * @example
 * <WhenView />
 */
export default function WhenView(props) {
  // Get the card id from the parameters and get all transactions associated with the selected card
  const model = useModel();
  const cardID = useParams().cardID ?? Util.getDefaultCardID(model);
  const transactions = Transaction.initAll().filter(item => item.card.id === cardID, []);
  // getOffsets :: [Transaction] -> OBJECT
  // This function goes through all transations and gets the month(s) and transactions associated
  // NOTE: Date is stored as the key in the format of "m/d/y"
  const getOffsets = transactions => transactions.reduce((result, item) => {
    const date = BCMSDate.init().setToFirst();

    date.month = item.purchaseDate.monthInt;
    date.year = item.purchaseDate.yearInt;

    const dateStr = date.format("M d, Y");

    if(result[dateStr] === undefined) { result[dateStr] = []; }
    return { ...result, [ dateStr ] : [ ...result[dateStr], item ] };
  }, {});
  // createGroup :: BCMSDate, [Transaction], STRING -> React.Component
  // Create a new MonthGroupDropdown component for the date and transactions passed
  const createGroup = (date, transactions, key) => <MonthGroupDropdown key={ `Month_${ key }` } date={ date } transactions={ transactions } />
  // Get the offsets of the transactions for the card id
  const offset = getOffsets(transactions);
  // For each month transactions exist for, create a new group dropdown
  const groups = Object.keys(offset).reduce((result, key) => {
    const date = BCMSDate.init(new Date(key));
    return [ ...result, createGroup(date, offset[key], key) ];
  }, []);

  return (
    <div className="when-view">
      <div className="info-group custom-grid-container">
        <div className="graph-container" data-point="Spend_Insights_When_Graph">
          <div className="chart-asset custom-grid-container bottom-border">
            <div className="column col-2 flex-container flex-vertical">
              <span className="amount-text">$400</span>
              <div className="bar" />
            </div>
            <div className="column col-3 flex-container flex-vertical">
              <span className="amount-text">$3,000</span>
              <div className="bar" />
            </div>
            <div className="column col-4 flex-container flex-vertical">
              <span className="amount-text">$400</span>
              <div className="bar" />
            </div>
            <div className="column col-5 flex-container flex-vertical">
              <span className="amount-text">$300</span>
              <div className="bar" />
            </div>
            <div className="column col-6 flex-container flex-vertical">
              <span className="amount-text">$284.79</span>
              <div className="bar" />
            </div>
            <div className="column col-7 flex-container flex-vertical active-bar">
              <span className="amount-text">$1,373</span>
              <div className="bar" />
            </div>
          </div>
        </div>
        <MonthTabsContainer />
      </div>
      
      <MainContentSection text="Recent Transactions">
        { groups }
      </MainContentSection>
    </div>
  );
}

/**
 * @function MonthGroupDropdown
 * @param {STRING} monthLetter - The first letter of each month
 * @param {BCMSDate} date - The date to determine the month of the group
 * @param {ARRAY} transactions - The transactions in the same month as the passe date
 * @returns {React.Component}
 * @exported false
 * @description Creates a toggleable dropdown for the transactions of a certain month
 * @example
 * <MonthGroupDropdown date={ BCMSDate.init() } transactions={ [] } />
 */
function MonthGroupDropdown(props) {
  const cardID = useParams().cardID ?? "0";
  const navigate = useRoute();
  // Set the defaults for the date and transactions
  const monthLetter = props.monthLetter ?? "J";
  const date = props.date ?? BCMSDate.init();
  const transactions = props.transactions ?? [];
  // Sum all the amounts of each transaction
  const totalAmount = transactions.reduce((result, item) => {
    const amount = parseFloat(item.amount.amount);
    return (result + (item.credit === "true" ? -amount : amount));
  }, 0);

  // Create a new state to control if the dropdown content is shown or not
  const [ state, setState ] = useState(false);
  const toggle = evt => setState(!state);
  // For each transaction create a new TransactionView component
  const transactionItems = transactions.sort((a, b) => {
      if(a.purchaseDate.isBefore(b.purchaseDate)) { return 1; }
      else if(a.purchaseDate.isAfter(b.purchaseDate)) { return -1; }
      else { return 0; }
    })
    .map(transaction => {
      const isCredit = transaction.credit === "true";
      const onClick = transaction.merchant.name !== "Payment" ?
      evt => navigate(`/home/transactiondetails/${cardID}/${transaction.id}`)
      : () => {};

      return (
        <TransactionView
          key={ `Transaction_${ transaction.id }`}
          billerName={ transaction.merchant.name }
          date={ transaction.purchaseDate.format("M d, Y") }
          amount={ `${ isCredit ? "+" : "" }${ transaction.amount.format() }` }
          positiveClassName={ isCredit ? "is-credit" : "" }
          leftIcon={ transaction.merchant.logo }
          onClick={ onClick } />
      );
    }, []);

  return (
    <GroupDropdownView
      onClick={ toggle }
      state={ state }
      logo={ 
        <div className="month-icon-container">
          <SVGAsset id="MonthCalendarIcon" className="group-icon" />
          <p className="month-letter text-center">{monthLetter}</p>
        </div>
      }
      name={ date.format("F Y") }
      className={ totalAmount < 0 ? "amount-is-credit" : "" }
      totalAmount={ BCMSCurrency.toCurrency(totalAmount).replace("-", "+") }>

      { transactionItems }
    </GroupDropdownView>
  );
}